/**
 * CelebrationModal — Hiệu ứng chúc mừng sau verify/purchase/milestone
 * + DataShredder — Animation xóa dữ liệu sau khi dịch xong
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle, Shield, ShieldCheck } from 'lucide-react';

// ============================================================
// Celebration Modal (sau verify phone / mua pack)
// ============================================================

interface CelebrationModalProps {
  isOpen: boolean;
  title: string;
  subtitle: string;
  value?: string;
  onClose: () => void;
}

export const CelebrationModal: React.FC<CelebrationModalProps> = ({ isOpen, title, subtitle, value, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center animate-in zoom-in-95 fade-in duration-300">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-emerald-600" />
        </div>
        <h3 className="text-xl font-black mb-2">{title}</h3>
        {value && <p className="text-3xl font-black text-indigo-600 mb-2">{value}</p>}
        <p className="text-sm text-slate-500 mb-6">{subtitle}</p>
        <button
          onClick={onClose}
          className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 active:scale-95 transition-all"
        >
          Tiếp tục
        </button>
      </div>
    </div>
  );
};

// ============================================================
// Data Shredder (privacy animation sau khi dịch xong)
// ============================================================

interface DataShredderProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DataShredder: React.FC<DataShredderProps> = ({ isOpen, onClose }) => {
  const [phase, setPhase] = useState<'shredding' | 'done'>('shredding');

  useEffect(() => {
    if (isOpen) {
      setPhase('shredding');
      const timer = setTimeout(() => setPhase('done'), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center px-4" onClick={e => e.target === e.currentTarget && phase === 'done' && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-8 text-center">
        <div className={`mb-4 ${phase === 'shredding' ? 'animate-pulse' : ''}`}>
          {phase === 'shredding' ? (
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto">
              <Shield className="w-8 h-8 text-amber-600" />
            </div>
          ) : (
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
              <ShieldCheck className="w-8 h-8 text-emerald-600" />
            </div>
          )}
        </div>
        <h4 className="font-black mb-2">
          {phase === 'shredding' ? 'Đang tiêu hủy dữ liệu...' : 'Đã tiêu hủy hoàn toàn'}
        </h4>
        <p className="text-xs text-slate-500 mb-4">
          {phase === 'shredding'
            ? 'Xóa vĩnh viễn nội dung tài liệu khỏi server...'
            : 'Không ai có thể truy cập nội dung tài liệu của bạn'}
        </p>
        {phase === 'done' && (
          <button onClick={onClose} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl text-sm hover:bg-emerald-700 active:scale-95">
            Đã hiểu
          </button>
        )}
      </div>
    </div>
  );
};
